import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { ROCKET_STANDARD_SLIDES } from '@/components/Deals/ProposalStudio/rocketStandardSlides'
import { readProposalPresentation } from '@/utils/proposalPresentationSnapshot'
import { ProposalPresentationFrame } from './ProposalPresentationFrame'
import { ProposalPresentationPager } from './ProposalPresentationPager'

export default function ProposalPresentationPage() {
  const snapshot = useMemo(() => readProposalPresentation(), [])
  const total = ROCKET_STANDARD_SLIDES.length + (snapshot ? 1 : 0)
  const rootRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)
  const [fullscreen, setFullscreen] = useState(false)
  const [cover, setCover] = useState(false)
  const immersive = fullscreen || cover

  const go = useCallback(
    (direction: 1 | -1) => {
      setIndex((current) => Math.min(total - 1, Math.max(0, current + direction)))
    },
    [total],
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ') {
        event.preventDefault()
        go(1)
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault()
        go(-1)
      } else if (event.key === 'Escape') {
        setCover(false)
      } else if (event.key === 'Home') {
        setIndex(0)
      } else if (event.key === 'End') {
        setIndex(Math.max(0, total - 1))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, total])

  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === rootRef.current)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])

  if (!snapshot) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#f4f6f8] p-6 text-center text-[#3a3a3a]">
        <p>Abra a apresentação pelo Proposal Studio.</p>
        <Link to="/deals" className="text-sm font-semibold text-[var(--brand-primary)]">
          Voltar às negociações
        </Link>
      </div>
    )
  }

  const present = () => {
    const node = rootRef.current
    if (!node?.requestFullscreen) {
      setCover(true)
      return
    }
    void node.requestFullscreen().then(() => {
      if (document.fullscreenElement !== node) setCover(true)
    }).catch(() => setCover(true))
  }

  const leave = () => {
    setCover(false)
    if (document.fullscreenElement) void document.exitFullscreen()
  }

  return (
    <div
      ref={rootRef}
      className={
        immersive
          ? 'fixed inset-0 z-50 h-screen w-screen bg-black'
          : 'min-h-screen bg-[#f4f6f8]'
      }
    >
      <div className={immersive ? 'h-full' : 'mx-auto flex min-h-screen max-w-[1200px] flex-col px-6 pt-8'}>
        <div className={immersive ? 'h-full' : 'flex flex-1 items-center'}>
          <ProposalPresentationFrame
            index={index}
            value={snapshot.value}
            immersive={immersive}
            onAdvance={go}
          />
        </div>
        {immersive ? (
          <button
            type="button"
            aria-label="Sair da apresentação"
            onClick={leave}
            className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white"
          >
            <X size={18} />
          </button>
        ) : (
          <ProposalPresentationPager
            index={index}
            total={total}
            onPrev={() => go(-1)}
            onNext={() => go(1)}
            onPresent={present}
          />
        )}
      </div>
    </div>
  )
}
